import { useEffect, useRef, useState, type ReactNode } from "react";
import type { OperatorIdentity, OperatorAudit } from "@proof/operator";
import { Button, Control } from "./components";
import {
  instanceRequest,
  instanceMessage,
  InstanceRequestError,
  type InstanceStatus,
} from "./instance-api";
import { safeOperatorReturn } from "./setup-model";
export function operatorNavigate(path: string, replace = false) {
  history[replace ? "replaceState" : "pushState"](null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
function Panel({ title, children }: { title: string; children: ReactNode }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, [title]);
  return (
    <main className="instance-shell instance-access">
      <header className="instance-brand">
        Proof Studio <span>Platform operator</span>
      </header>
      <section className="instance-panel">
        <h1 ref={heading} tabIndex={-1}>
          {title}
        </h1>
        {children}
      </section>
    </main>
  );
}
export function OperatorLogin({ onChanged }: { onChanged: () => void }) {
  const [username, setUsername] = useState(""),
    [password, setPassword] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <Panel title="Operator sign in">
      <p>Platform access is separate from tenant accounts.</p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError("");
          void instanceRequest("/api/operator/login", { username, password })
            .then(() => {
              setPassword("");
              operatorNavigate(
                safeOperatorReturn(
                  new URLSearchParams(location.search).get("return"),
                ),
              );
              onChanged();
            })
            .catch((cause) => setError(instanceMessage(cause)))
            .finally(() => setBusy(false));
        }}
      >
        <fieldset disabled={busy}>
          <Control label="Operator username">
            <input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              maxLength={128}
              required
            />
          </Control>
          <Control label="Operator password">
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              maxLength={128}
              required
            />
          </Control>
          {error && (
            <p role="alert" className="instance-error">
              {error}
            </p>
          )}
          <Button type="submit" kind="primary">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </fieldset>
      </form>
    </Panel>
  );
}
export function OperatorPassword({
  forced,
  onChanged,
}: {
  forced: boolean;
  onChanged: () => void;
}) {
  const [currentPassword, setCurrent] = useState(""),
    [nextPassword, setNext] = useState(""),
    [confirm, setConfirm] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <Panel
      title={forced ? "Change temporary password" : "Change operator password"}
    >
      <p>
        {forced
          ? "Choose a new password before platform access is available."
          : "Changing your password signs out every other session for your account."}
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (
            nextPassword.length < 12 ||
            nextPassword.length > 128 ||
            nextPassword !== confirm ||
            nextPassword === currentPassword
          ) {
            setError(
              "Use a different 12–128 character password and matching confirmation.",
            );
            return;
          }
          setBusy(true);
          setError("");
          void instanceRequest("/api/operator/password", {
            currentPassword,
            nextPassword,
          })
            .then(() => {
              setCurrent("");
              setNext("");
              setConfirm("");
              operatorNavigate(
                safeOperatorReturn(
                  new URLSearchParams(location.search).get("return"),
                ),
              );
              onChanged();
            })
            .catch((cause) => {
              setError(instanceMessage(cause));
              if (cause instanceof InstanceRequestError && cause.status === 401)
                onChanged();
            })
            .finally(() => setBusy(false));
        }}
      >
        <fieldset disabled={busy}>
          <Control label="Current password">
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrent(event.target.value)}
              autoComplete="current-password"
              maxLength={128}
              required
            />
          </Control>
          <Control label="New password">
            <input
              type="password"
              value={nextPassword}
              onChange={(event) => setNext(event.target.value)}
              autoComplete="new-password"
              minLength={12}
              maxLength={128}
              required
            />
          </Control>
          <Control label="Confirm new password">
            <input
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="new-password"
              minLength={12}
              maxLength={128}
              required
            />
          </Control>
          {error && (
            <p role="alert" className="instance-error">
              {error}
            </p>
          )}
          <div className="instance-actions">
            <Button type="submit" kind="primary">
              {busy ? "Updating…" : "Change password"}
            </Button>
            {!forced && (
              <Button onClick={() => operatorNavigate("/operator")}>
                Cancel
              </Button>
            )}
          </div>
        </fieldset>
      </form>
    </Panel>
  );
}
interface View {
  canEditConfiguration?: boolean;
  configuration: NonNullable<InstanceStatus["configuration"]>;
  queue: {
    ready: boolean;
    pending: number;
    active: number;
    concurrency: number;
  };
}
interface Row {
  id: string;
  tenantId: string;
  projectId: string;
  state: string;
  attempts: number;
}
export function OperatorHome({
  identity,
  renders,
  onChanged,
}: {
  identity: OperatorIdentity;
  renders: boolean;
  onChanged: () => void;
}) {
  const [view, setView] = useState<View>(),
    [rows, setRows] = useState<Row[]>([]),
    [audits, setAudits] = useState<OperatorAudit[]>([]),
    [error, setError] = useState("");
  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    const load = async () => {
      try {
        const [status, events, jobs] = await Promise.all([
          instanceRequest<View>(
            "/api/operator/status",
            undefined,
            undefined,
            controller.signal,
          ),
          instanceRequest<{ records: OperatorAudit[] }>(
            "/api/operator/audit?limit=20",
            undefined,
            undefined,
            controller.signal,
          ),
          renders
            ? instanceRequest<{ records: Row[] }>(
                "/api/operator/renders",
                undefined,
                undefined,
                controller.signal,
              )
            : Promise.resolve({ records: [] }),
        ]);
        if (current) {
          setView(status);
          setAudits(events.records);
          setRows(jobs.records);
        }
      } catch (cause) {
        if (current) {
          setView(undefined);
          setRows([]);
          setAudits([]);
          setError(instanceMessage(cause));
          onChanged();
        }
      }
    };
    void load();
    const timer = setInterval(() => void load(), 10000);
    return () => {
      current = false;
      controller.abort();
      clearInterval(timer);
    };
  }, [renders, identity.operatorId]);
  return (
    <main className="instance-shell">
      <header className="instance-brand">
        Proof Studio <span>Platform operator</span>
      </header>
      <section className="instance-panel">
        <div className="instance-heading">
          <div>
            <p className="instance-eyebrow">Signed in as {identity.username}</p>
            <h1>{renders ? "Render operations" : "Platform status"}</h1>
          </div>
          <div className="instance-actions">
            {view?.canEditConfiguration && (
              <Button
                onClick={() => operatorNavigate("/operator/configuration")}
              >
                Instance configuration
              </Button>
            )}
            <Button onClick={() => operatorNavigate("/operator/impersonation")}>
              Tenant support access
            </Button>
            <Button
              onClick={() =>
                operatorNavigate(renders ? "/operator" : "/admin/renders")
              }
            >
              {renders ? "Platform status" : "Render activity"}
            </Button>
            <Button
              onClick={() =>
                operatorNavigate(
                  `/operator/password?return=${encodeURIComponent(renders ? "/admin/renders" : "/operator")}`,
                )
              }
            >
              Change password
            </Button>
            <Button
              onClick={() =>
                void instanceRequest("/api/operator/logout", {})
                  .then(() => {
                    operatorNavigate("/operator/login");
                    onChanged();
                  })
                  .catch((cause) => {
                    setError(instanceMessage(cause));
                    onChanged();
                  })
              }
            >
              Log out
            </Button>
          </div>
        </div>
        <p className="instance-note">
          Platform authority does not grant tenant membership. Tenant workspace
          and resource navigation are unavailable in this session.
        </p>
        {error && <p role="alert">{error}</p>}
        {!view && !error && <p role="status">Loading platform status…</p>}
        {view && (
          <>
            <dl className="instance-summary">
              <dt>Instance</dt>
              <dd>{view.configuration.name}</dd>
              <dt>Revision</dt>
              <dd>{view.configuration.revision}</dd>
              <dt>Providers</dt>
              <dd>
                {Object.entries(view.configuration.providers)
                  .map(([name, kind]) => `${name}: ${kind}`)
                  .join(" · ")}
              </dd>
              <dt>Renderer</dt>
              <dd>
                {view.queue.ready ? "Ready" : "Unavailable"} ·{" "}
                {view.queue.active} active · {view.queue.pending} queued ·{" "}
                {view.queue.concurrency} slots
              </dd>
            </dl>
            {renders && (
              <>
                <p>
                  Latest 50 jobs. Values and artifacts are excluded from this
                  monitor.
                </p>
                <div
                  className="instance-table"
                  role="region"
                  aria-label="Render activity"
                >
                  <table>
                    <thead>
                      <tr>
                        <th>Job</th>
                        <th>Tenant</th>
                        <th>Project</th>
                        <th>State</th>
                        <th>Attempts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>{row.tenantId}</td>
                          <td>{row.projectId}</td>
                          <td>{row.state}</td>
                          <td>{row.attempts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!rows.length && <p>No render jobs.</p>}
                </div>
              </>
            )}
            <h2>Recent operator activity</h2>
            <ul className="instance-audit">
              {audits.map((event) => (
                <li key={event.id}>
                  <time>{new Date(event.at).toLocaleString()}</time>
                  <span>{event.kind.replaceAll("_", " ")}</span>
                  <span>{event.username || "Unknown account"}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </main>
  );
}
